import { PluginUtils } from 'owa-editor';
import { isFeatureEnabled } from 'owa-feature-flags';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import { trace } from 'owa-trace';
import { isBrowserChrome, isBrowserEDGECHROMIUM } from 'owa-user-agent';
import { Editor } from 'roosterjs-editor-core';
import { ContentTraverser } from 'roosterjs-editor-dom';
import { BlockElement, PluginKeyDownEvent, PositionType } from 'roosterjs-editor-types';
import isReadonlyBeautifulLink from '../utils/isReadonlyBeautifulLink';
import { LINK_TEMP_ZERO_WIDTH_SPACE_CLASS } from './constants';

export function processKeyDownEvent(event: PluginKeyDownEvent, utils: PluginUtils, editor: Editor) {
    switch (event.rawEvent.which) {
        case KeyboardCharCodes.Down_arrow:
            processDownArrow(event, utils, editor);
            break;
        case KeyboardCharCodes.Backspace:
            processBackspace(event, utils, editor);
            break;
        case KeyboardCharCodes.Delete:
            processDelete(event, utils, editor);
            break;
        default:
            return;
    }
}

function processDownArrow(event: PluginKeyDownEvent, utils: PluginUtils, editor: Editor) {
    const searcher = utils.cacheGetContentSearcher(event, editor);
    const inline = searcher.getInlineElementAfter();
    const inlineBefore = searcher.getInlineElementBefore();
    const selectionNode = editor.getSelectionRange();
    // Following is fixing the bug that the down-arrow is stuck in front of beautiful link.
    // It is a weird browser behavior triggered by content-editable='false'.
    if (!inlineBefore && inline && selectionNode && selectionNode.collapsed) {
        const containerNode: Node = inline.getContainerNode();
        if (isReadonlyBeautifulLink(containerNode)) {
            trace.info('down arrow to move select to next line');
            const bodyTraverser: ContentTraverser = editor.getBodyTraverser(containerNode);
            if (bodyTraverser) {
                const nextBlockElement: BlockElement = bodyTraverser.getNextBlockElement();
                if (nextBlockElement) {
                    editor.select(nextBlockElement.getStartNode(), PositionType.Before);
                    event.rawEvent.preventDefault();
                }
            }
        }
    }
}

function processBackspace(event: PluginKeyDownEvent, utils: PluginUtils, editor: Editor) {
    const searcher = utils.cacheGetContentSearcher(event, editor);
    const inline = searcher.getInlineElementBefore();
    const selectionNode = editor.getSelectionRange();
    if (inline && selectionNode && selectionNode.collapsed) {
        // Handling the backspace at the end of readonly beautiful link to remove it.
        // It is a weird browser behavior triggered by content-editable='false'.
        const containerNode: Node = inline.getContainerNode();
        if (isReadonlyBeautifulLink(containerNode)) {
            editor.deleteNode(containerNode);
            utils.clearContentSearcherCache(event);
            event.rawEvent.preventDefault();
            return;
        }
    }

    if (shouldAddAndRemoveTempZeroSpace()) {
        addAndRemoveTempZeroSpace(editor);
    }
}

function processDelete(event: PluginKeyDownEvent, utils: PluginUtils, editor: Editor) {
    if (shouldAddAndRemoveTempZeroSpace()) {
        const searcher = utils.cacheGetContentSearcher(event, editor);
        const inline = searcher.getInlineElementAfter();
        const inlineBefore = searcher.getInlineElementBefore();
        const selectionNode = editor.getSelectionRange();
        // If there is nothing in front of the cursor and beautiful link is after the cursor,
        // do nothing
        if (!inlineBefore && inline && selectionNode && selectionNode.collapsed) {
            const containerNode: Node = inline.getContainerNode();
            if (isReadonlyBeautifulLink(containerNode)) {
                return;
            }
        }

        addAndRemoveTempZeroSpace(editor);
    }
}

function shouldAddAndRemoveTempZeroSpace() {
    return (
        isFeatureEnabled('doc-attachment-contextualSharingTips') &&
        (isBrowserChrome() || isBrowserEDGECHROMIUM())
    );
}

/*
Another weird browser behavior triggered by content-editable='false'.
When there is some text in front of the beautiful link, user puts the cursor in the beginning of line and hit backspace,
the browser would delete the beautiful link.
If user puts the cursor in the end of front line and hits delete, the beautiful link will also be removed.
You may not repro it 100%, but it happens quite often.
*/
function addAndRemoveTempZeroSpace(editor: Editor) {
    editor.queryElements('[contenteditable="false"]', readonlyNode => {
        const node = editor.getDocument().createElement('span');
        node.innerHTML = '\u200b';
        node.className = LINK_TEMP_ZERO_WIDTH_SPACE_CLASS;
        readonlyNode.parentNode.insertBefore(node, readonlyNode.nextSibling);
    });
    editor.runAsync(() => {
        if (!editor.isDisposed()) {
            editor.queryElements('.' + LINK_TEMP_ZERO_WIDTH_SPACE_CLASS, node => {
                if (node.parentNode) {
                    node.parentNode.removeChild(node);
                }
            });
        }
    });
}
